/**
 * TypedJsonProperty<T> — A typed wrapper around ReactiveProperty<PropertyValueData>
 * for JSON-serialized property values.
 *
 * The property system stores JSON values as serialized strings. This wrapper
 * transparently encodes/decodes so consumers work with typed objects.
 *
 * Also handles the transition period where old DB data may still contain
 * raw objects (not yet stringified).
 */

import { ReactiveProperty, Subscription, PropertyListener } from './ReactiveProperty';
import { PropertyValueData } from '../types';

/**
 * Interface satisfied by both ReactiveProperty<T> and TypedJsonProperty<T>,
 * allowing usePropertyValue and other generic consumers to accept either.
 */
export interface ReadableProperty<T> {
  value: T;
  subscribe(callback: PropertyListener<T>): Subscription;
  subscribeToChanges(callback: PropertyListener<T>): Subscription;
}

/**
 * Decode a raw property value to a typed object.
 * Handles both string (new format) and raw object (old DB data during transition).
 */
export function decodeJsonProperty<T>(raw: unknown): T {
  if (typeof raw === 'string') return JSON.parse(raw) as T;
  return raw as T;
}

/**
 * Encode a typed object to a JSON string for storage.
 */
export function encodeJsonProperty<T>(value: T): string {
  return JSON.stringify(value);
}

/**
 * Typed wrapper around a ReactiveProperty that stores JSON-serialized strings.
 * Provides transparent encode/decode so consumers work with typed objects.
 */
export class TypedJsonProperty<T> implements ReadableProperty<T> {
  private _source: ReactiveProperty<PropertyValueData>;
  private _cachedRaw: unknown = undefined;
  private _cachedParsed: T | undefined = undefined;

  constructor(source: ReactiveProperty<PropertyValueData>) {
    this._source = source;
  }

  get value(): T {
    const raw = this._source.value;
    if (raw !== this._cachedRaw) {
      this._cachedRaw = raw;
      this._cachedParsed = decodeJsonProperty<T>(raw);
    }
    return this._cachedParsed as T;
  }

  set value(newValue: T) {
    this._cachedRaw = undefined;
    this._cachedParsed = undefined;
    this._source.value = encodeJsonProperty(newValue);
  }

  next(newValue: T): void {
    this._cachedRaw = undefined;
    this._cachedParsed = undefined;
    this._source.next(encodeJsonProperty(newValue));
  }

  subscribe(callback: PropertyListener<T>): Subscription {
    return this._source.subscribe((raw) => {
      callback(decodeJsonProperty<T>(raw));
    });
  }

  subscribeToChanges(callback: PropertyListener<T>): Subscription {
    return this._source.subscribeToChanges((raw) => {
      callback(decodeJsonProperty<T>(raw));
    });
  }

  get subscriberCount(): number {
    return this._source.subscriberCount;
  }

  destroy(): void {
    this._cachedRaw = undefined;
    this._cachedParsed = undefined;
    this._source.destroy();
  }

  get isDestroyed(): boolean {
    return this._source.isDestroyed;
  }
}
